import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditClientModal from 'components/admin/common/modal/management/EditClientModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const Clients = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createClientData,
        postError: creatingError,
        postLoading: creating,
        putData: updateClientData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteClientData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.CLIENTS);

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteClient = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditClient = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteClient, modalData: clientToDelete, openModal: openDeleteClient, closeModal: closedDeleteClient } = useModalState({
        onClose: handleCloseDeleteClient
    });

    const { isOpen: isOpenEditClient, modalData: selectedClient, openModal: openEditClient, closeModal: closedEditClient } = useModalState({
        onClose: handleCloseEditClient
    });

    const validationRules = [
        { field: 'name', method: 'required' },
        { field: 'description', method: 'required' },
        { field: 'client_type', method: 'required' }
    ];

    const handleModalSave = useCallback(async (updatedClient) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedClient, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedClient.id) {
                await updateClientData(updatedClient.id, updatedClient, true);
                setSuccessMessage("Cliente actualizado con éxito.");
            } else {
                await createClientData(updatedClient, true);
                setSuccessMessage("Cliente creado con éxito.");
            }
            closedEditClient();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateClientData, createClientData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newClient = {
            name: '',
            description: '',
            client_type: ''
        };
        openEditClient(newClient);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (clientToDelete) {
            try {
                await deleteClientData(clientToDelete.id);
                setSuccessMessage("Cliente eliminado con éxito.");
                refetch();
                closedDeleteClient();
            } catch (error) {
                console.error(error);
            }
        }
    }, [clientToDelete, deleteClientData, refetch]);

    const columns = useMemo(() => [
        { field: 'name', header: 'Nombre', sortable: true },
        { field: 'description', header: 'Descripción', sortable: true },
        { field: 'client_type', header: 'Tipo de Cliente', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Clientes</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Cliente
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditClient}
                onDelete={openDeleteClient}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="name"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditClientModal
                show={isOpenEditClient}
                client={selectedClient}
                handleClose={closedEditClient}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteClient}
                onHide={closedDeleteClient}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Clients);